import React, {useState, useEffect, useMemo, MouseEvent, FC} from 'react';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';

import useLocalization from '../../../../../hooks/useLocalization';
import DatePickerOption from '../../../../common/date-range-picker/DatePickerOption';
import {MonthRangePicker, useRangePickerData} from '../../../../../components';
import {
    getDateline,
    GlobalDatePickerProps,
    DatePickerContainer,
    isSameDateRange
} from '../../../../global-date-picker';
import PopperInput from '../../../../common/PopperInput';
import useTimeRangeWarning from '../components/time-range-limit/useTimeRangeWarning';
import {StyledPopover} from '../../../../widget-date-picker/WidgetDatePicker';
import {REWARDS_PRESELECTED_DATE_RANGE, DateRangePickerOption} from '../constants';

import {TimeScale} from '../../../../../types';

const CODES = {
    [DateRangePickerOption.PRESELECTED]:
        'epic.affiliate.fortnite_games.rewards.date_range.preselected',
    [DateRangePickerOption.CUSTOM]: 'epic.affiliate.fortnite_games.rewards.date_range.custom'
};

export interface RewardsDatePickerProps extends Omit<GlobalDatePickerProps, 'onChange' | 'value'> {
    timeScale: TimeScale;
}

const RewardsDatePicker: FC<RewardsDatePickerProps> = ({
    timeScale,
    selectedDate: selected,
    startDate: start,
    endDate: end,
    onRangeChange
}: RewardsDatePickerProps) => {
    const {
        isPreselection,
        selectedDate,
        startDate,
        endDate,
        setIsPreselection,
        handleMonthChange,
        setCalendar
    } = useRangePickerData({
        selectedDate: selected,
        startDate: start,
        endDate: end
    });
    const getMessage = useLocalization();
    const [checkTimeRange] = useTimeRangeWarning();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dateRange, setDateRange] = useState(DateRangePickerOption.PRESELECTED);
    const isNewDateRange = !isSameDateRange({startDate: start, endDate: end}, {startDate, endDate});

    const open = Boolean(anchorEl);
    const id = open ? 'date-picker-popper' : undefined;

    const dateline = getDateline({startDate, endDate});

    const dateRangeOptions = useMemo(() => {
        return Object.values(DateRangePickerOption).map(value => ({
            label: getMessage(CODES[value]),
            value
        }));
    }, []);

    const handleOptionChange = (value: DateRangePickerOption) => {
        setDateRange(value);
        if (value === DateRangePickerOption.PRESELECTED) {
            setCalendar(REWARDS_PRESELECTED_DATE_RANGE);
        }
    };

    const handleCalendarChange = value => {
        setDateRange(DateRangePickerOption.CUSTOM);
        handleMonthChange(value);
    };

    const togglePopoverState = (event: MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            if (isNewDateRange) {
                checkTimeRange(
                    {startDate, endDate, timeScale},
                    {
                        onConfirm: () => setAnchorEl(null)
                    }
                );
            } else {
                setAnchorEl(null);
            }
            return;
        }

        setAnchorEl(event.currentTarget);
    };

    useEffect(() => {
        setCalendar({startDate: start, endDate: end, selectedDate: selected});
    }, [start, end, selected]);

    useEffect(() => {
        if (!open) {
            setIsPreselection(true);
            onRangeChange({startDate, endDate, selectedDate});
        }
    }, [open]);

    return (
        <>
            <PopperInput open={open} value={dateline} onClick={togglePopoverState} />
            <StyledPopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={togglePopoverState}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}>
                <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                    <DatePickerContainer>
                        <Box my={4}>
                            {dateRangeOptions.map((option, i) => (
                                <React.Fragment key={option.value}>
                                    <DatePickerOption
                                        selected={dateRange === option.value}
                                        onChange={handleOptionChange}
                                        {...option}
                                    />
                                    {i === 0 && (
                                        <Box mx={4} my={1}>
                                            <Divider />
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                        <MonthRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleCalendarChange}
                            isPreselection={isPreselection}
                        />
                    </DatePickerContainer>
                </MuiPickersUtilsProvider>
            </StyledPopover>
        </>
    );
};

export default RewardsDatePicker;
